import { router } from '@inertiajs/react';
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import type { UserRole } from '@/types/shared';

// Register text-h* as font-sizes so <Heading3 className="text-xl" /> drops text-h3.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// crypto.randomUUID is only exposed in secure contexts; dev runs on http *.lvh.me.
export function randomId(): string {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `id-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export function getInitials(firstNameOrFullName?: string, lastName?: string): string {
  // If only one argument is provided, treat it as a full name and split it
  if (lastName === undefined) {
    if (!firstNameOrFullName) {
      return '?';
    }
    const parts = firstNameOrFullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      // Full name: take first letter of first and last parts
      return `${parts[0]?.charAt(0) ?? ''}${parts[parts.length - 1]?.charAt(0) ?? ''}`
        .toUpperCase()
        .slice(0, 2);
    }
    // Single word: just return first letter
    return parts[0]?.charAt(0).toUpperCase() || '?';
  }

  // Two arguments: treat as firstName and lastName
  const initials = `${firstNameOrFullName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.slice(
    0,
    2,
  );
  return initials.toUpperCase() || '?';
}

export function capitalize(label: string): string {
  if (!label) {
    return '';
  }
  return label
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function formatRoleLabel(role: UserRole): string {
  const normalizedRole = role.toLowerCase();
  if (normalizedRole === 'root') {
    return 'Owner';
  }
  return capitalize(normalizedRole);
}

export const isServer = typeof window === 'undefined';

export function isBrowser(): boolean {
  return !isServer;
}

export function getLocalStorage(key: string): string | null {
  if (isServer) return null;

  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setLocalStorage(key: string, value: string): boolean {
  if (isServer) return false;

  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeLocalStorage(key: string): boolean {
  if (isServer) return false;

  try {
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function getSessionStorage(key: string): string | null {
  if (isServer) return null;

  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setSessionStorage(key: string, value: string): boolean {
  if (isServer) return false;

  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function getOpenerWindow(): Window | null {
  if (isServer) return null;
  try {
    const opener = window.opener as Window | null;
    if (opener && !opener.closed) {
      return opener;
    }
  } catch {
    // Cross-origin or closed window
  }
  return null;
}

export function getOpenerSessionStorage(key: string): string | null {
  const opener = getOpenerWindow();
  if (!opener) return null;
  try {
    return opener.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function setOpenerSessionStorage(key: string, value: string): boolean {
  const opener = getOpenerWindow();
  if (!opener) return false;
  try {
    opener.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function matchMedia(query: string): MediaQueryList | null {
  if (isServer || !window.matchMedia) return null;
  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}

export function elementFromEventTarget(target: EventTarget | null): Element | null {
  if (isServer) return null;

  if (typeof Element !== 'undefined' && target instanceof Element) {
    return target;
  }
  if (typeof Node !== 'undefined' && target instanceof Node) {
    return target.parentElement;
  }

  return null;
}

export function getDocument(): Document | null {
  if (isServer) return null;

  return window.document;
}

export function getCsrfToken(): string {
  const cookie = getDocument()?.cookie ?? '';
  const match = cookie.match(/XSRF-TOKEN=([^;]+)/);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return '';
  }
}

export function addWindowEventListener<T extends Event = Event>(
  event: string,
  handler: (event: T) => unknown,
  options?: boolean | { capture?: boolean; once?: boolean; passive?: boolean },
): (() => void) | null {
  if (isServer) return null;
  window.addEventListener(event, handler as (event: Event) => void, options);
  return () => window.removeEventListener(event, handler as (event: Event) => void, options);
}

export function removeWindowEventListener<T extends Event = Event>(
  event: string,
  handler: (event: T) => unknown,
  options?: boolean | { capture?: boolean; once?: boolean; passive?: boolean },
): void {
  if (isServer) return;
  window.removeEventListener(event, handler as (event: Event) => void, options);
}

export function openWindow(url: string, target?: string, features?: string): Window | null {
  if (isServer) return null;

  return window.open(url, target, features);
}

export function reloadPage(): void {
  if (isServer) return;
  router.reload();
}

export function confirmDialog(message: string): boolean {
  if (isServer) return false;
  return window.confirm(message);
}

export function navigateTo(url: string): void {
  if (isServer) return;
  router.visit(url);
}

export function parseUrl(url: string): {
  pathname: string;
  searchParams: URLSearchParams;
} {
  const [pathname, searchString = ''] = url.split('?');
  return {
    pathname,
    searchParams: new URLSearchParams(searchString),
  };
}

export function normalizeRootRelativePath(path: string): string {
  const trimmed = path.trim();
  return `/${trimmed.replace(/^\/+/, '')}`;
}

export function getQueryParam(url: string, param: string): string | null {
  const { searchParams } = parseUrl(url);
  return searchParams.get(param);
}

export function removeQueryParams(url: string, paramsToRemove: string[]): string {
  const { pathname, searchParams } = parseUrl(url);
  for (const param of paramsToRemove) {
    searchParams.delete(param);
  }
  const newSearch = searchParams.toString();
  return newSearch ? `${pathname}?${newSearch}` : pathname;
}

export function pinItemToTop<T>(
  items: T[],
  pinnedId: string | null | undefined,
  getId: (item: T) => string,
): T[] {
  if (!pinnedId) return items;
  let pinnedItem: T | undefined;
  const rest: T[] = [];
  for (const item of items) {
    if (pinnedItem === undefined && getId(item) === pinnedId) {
      pinnedItem = item;
    } else {
      rest.push(item);
    }
  }
  if (pinnedItem === undefined) return items;
  return [pinnedItem, ...rest];
}

/**
 * Format error messages from various error types into a human-readable string.
 * Handles string errors, object errors (extracts values), and provides a default message.
 */
export function formatErrorMessage(errors: unknown, defaultMessage = 'An error occurred'): string {
  if (typeof errors === 'string') {
    return errors;
  }
  if (errors instanceof Error && errors.message) {
    return errors.message;
  }
  if (errors && typeof errors === 'object') {
    const parts: string[] = [];
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (typeof value === 'string') {
        parts.push(value);
      } else if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string') parts.push(item);
        }
      }
    }
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }
  return defaultMessage;
}

/**
 * Post a message to the parent window (for iframe communication).
 * Only posts if running in an iframe context.
 */
export function postMessageToParent(message: unknown, targetOrigin?: string): void {
  if (isServer) return;
  const origin = targetOrigin ?? getDocument()?.location.origin;
  if (!origin) return;
  if (window.parent !== window) {
    window.parent.postMessage(message, origin);
  }
}
